import React, { useEffect, useState } from "react";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, onSnapshot, doc, setDoc, getDoc, Timestamp, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../../utils/firebase"; 
import './Posting.css';
import { toast } from 'react-toastify'; 
import 'react-toastify/dist/ReactToastify.css';  

const Posting = ({ location }) => {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);  
  const [expandedImage, setExpandedImage] = useState(null); // Store selected image
  const [currentPage, setCurrentPage] = useState(1);  // Pagination: Current page
  const postsPerPage = 5;  // Number of posts per page

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);


  useEffect(() => {
    if (!location) return;
    
  const unsubscribe = onSnapshot(
    query(collection(db, "posts"), where("location", "==", location)),
    (snapshot) => {
      const postsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Sort posts by createdAt field in descending order
      postsData.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());

      setPosts(postsData);
      setCurrentPage(1);  // Reset the current page to 1 whenever posts are updated
    }
  );
  return () => unsubscribe();
}, [location]);

const handlePost = async (e) => {
  e.preventDefault();

  if (rating === 0 && images.length === 0) {
    toast.error("Please provide at least a rating.", {
      position: "top-center",
      autoClose: 500,
      style: { backgroundColor: '#f44336', color: 'white' },
    });
    return;
  }

  setUploading(true);

  try {
    // Check if the user already has a post in the same location
    const postQuery = query(
      collection(db, "posts"),
      where("userId", "==", user.uid),
      where("location", "==", location)
    );

    const postSnapshot = await getDocs(postQuery);

    if (!postSnapshot.empty) { // Correct condition
      toast.error("You have already posted in this location!", {
        position: "top-center",
        autoClose: 5000,
        style: { backgroundColor: '#f44336', color: 'white' },
      });
      setUploading(false);
      return;
    }

    const storage = getStorage();
    const imageUrls = [];

    if (images.length > 0) {
      for (const image of images) {
        const storageRef = ref(storage, `posts/${user.uid}/${Date.now()}-${image.name}`);
        await uploadBytes(storageRef, image);
        const imageUrl = await getDownloadURL(storageRef);
        imageUrls.push(imageUrl);
      }
    }

    const userDocRef = doc(db, "Users", user.uid);
    const userDoc = await getDoc(userDocRef);
    const userData = userDoc.exists() ? userDoc.data() : {};

    const firstName = userData.firstName || "Anonymous";
    const lastName = userData.lastName || "";
    const profileImage = userData.profile_image || "/default-profile.jpg";

    const postData = {
      text,
      imageUrls,
      userId: user.uid,
      userName: `${firstName} ${lastName}`,
      profileImage,
      location,
      rating,
      createdAt: Timestamp.now(),
    };

    const postRef = doc(collection(db, "posts"));
    await setDoc(postRef, postData);

    setText("");
    setImages([]);
    setRating(0);

    toast.success("Your post has been uploaded successfully!", {
      position: "top-center",
      autoClose: 5000,
      style: { backgroundColor: '#4CAF50', color: 'white' },
    });

  } catch (error) {
    console.error("Error uploading post:", error);
    toast.error("Failed to upload post. Please try again.", {
      position: "top-center",
      autoClose: 5000,
      style: { backgroundColor: '#f44336', color: 'white' },
    });
  } finally {
    setUploading(false);
  }
};
  
  
    // Handle clicking an image to expand
    const handleImageClick = (imageUrl) => {
      setExpandedImage(imageUrl);
    };
  
    // Close the expanded image
    const handleCloseImage = () => {
      setExpandedImage(null);
    };
  
    // Close when ESC key is pressed
    useEffect(() => {
      const handleKeyDown = (event) => {
        if (event.key === "Escape") {
          handleCloseImage();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

  const handleImageChange = (e) => {
    setImages([...e.target.files]);
  };

  const handleRatingChange = (value) => {
    setRating(value);
  };

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = posts.slice(indexOfFirstPost, indexOfLastPost);

  const totalPages = Math.ceil(posts.length / postsPerPage);

  return (
    <div className="posting-container">
      <h2>Explore Bacoor - User Posts</h2>

      {user ? (
        <form onSubmit={handlePost}>
          <div className="rating-container">
            {[1, 2, 3, 4, 5].map((star) => (
              <span
                key={star}
                className={`star ${rating >= star ? "selected" : ""}`}
                onClick={() => handleRatingChange(star)}
              >
                &#9733;
              </span>
            ))}
          </div>

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Share your experience (optional)..."
            rows="4"
          />

          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
          />
          <button type="submit" disabled={uploading}>
            {uploading ? "Uploading..." : "Post"}
          </button>
        </form>
      ) : (
        <p style={{ textAlign: "center", color: "#555" }}>
          Log in to share your experience!
        </p>
      )}

      <div className="posts-list">
        {currentPosts.map((post) => (
          <div key={post.id} className="post-card">
            <div className="post-user-profile d-flex align-items-center">
              {post.profileImage ? (
                <img
                  src={post.profileImage}
                  alt="User Profile"
                  className="profile-image rounded-circle"
                  style={{ width: "50px", height: "50px" }}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center bg-light rounded-circle"
                  style={{ width: "50px", height: "50px" }}
                >
                  <i className="bi bi-person" style={{ fontSize: "24px" }}></i>
                </div>
              )}
              <p className="ms-2 mb-0"><strong>{post.userName}</strong></p>
            </div>

            {post.imageUrls?.map((url, index) => (
              <img 
                key={index} 
                src={url} 
                alt={`Post ${index + 1}`} 
                className={`post-image ${expandedImage === url ? "expanded" : ""}`} 
                onClick={() => handleImageClick(url)} 
                style={{ cursor: "pointer" }} 
              />
            ))}

            <div className="post-details">
              <p>{post.text}</p>
              <p className="post-date">{new Date(post.createdAt.toDate()).toLocaleString()}</p>
              <p className="post-rating">Rating: {post.rating} ★</p>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index + 1}
              className={`page-button ${currentPage === index + 1 ? "active" : ""}`}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </button>
          ))}
        </div>
      )}

      {/* Success Modal */}
      {showModal && (
      <div className="modal fade show" style={{ display: "block" }} tabIndex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">  {/* Centering the modal */}
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title" id="exampleModalLabel">Post Uploaded</h5>
        </div>
        <div className="modal-body">
          Your post has been uploaded successfully!
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
)}

    {/* Expanded Image View (Facebook-style) */}
    {expandedImage && (
        <div className="expanded-image-container" onClick={handleCloseImage}>
          <img src={expandedImage} alt="Expanded" className="expanded-image" />
        </div>
      )}

    </div>
  );
};

export default Posting;
